import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { CheckCircle2 } from "lucide-react";
import { Link } from "@/lib/router-compat";

const seo = {
  title: "Federal Employee SAINT TMS | Neuro Wellness TMS",
  description:
    "Federal employees: SAINT is a five-day accelerated TMS treatment for depression. Learn whether your FEHB plan may cover it and how we verify your benefits. Call 954-827-9793.",
  canonical: "/federal-employee-saint-tms",
  keywords:
    "federal employee SAINT TMS, FEHB SAINT coverage, SAINT TMS federal employee, accelerated TMS federal worker",
};

const relatedLinks = [
  { title: "Federal Employee Hub", href: "/federal-employee-mental-health" },
  {
    title: "Does Federal BCBS Cover SAINT?",
    href: "/does-federal-bcbs-cover-saint",
  },
  { title: "FEHB TMS Coverage", href: "/fehb-tms-coverage" },
  { title: "SAINT TMS Overview", href: "/saint" },
  { title: "Verify a SAINT Provider", href: "/find-a-verified-saint-provider" },
];

export default function FederalEmployeeSaintTms() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="Federal Employee SAINT"
      title="SAINT TMS for Federal Employees"
      subtitle="A five-day accelerated TMS treatment for depression — and what federal employees need to know about coverage."
    >
      <DecisionSection heading="What SAINT is — in plain terms">
        <p>
          SAINT is an FDA-cleared TMS treatment that compresses what's usually a
          six-week course into five consecutive days. Instead of measuring on
          your scalp to estimate where to aim, SAINT uses an MRI scan to map
          your brain individually and find the exact spot to target. Then it
          delivers ten short sessions per day over five days.
        </p>
        <p>
          Neuro Wellness TMS Centers of America is the only Verified SAINT
          Provider in Florida on Magnus Medical's official list — one of only
          seven in the United States.
        </p>
      </DecisionSection>

      <DecisionSection heading="Does your federal plan cover SAINT?">
        <p>
          The federal program's medical policy specifically names the SAINT
          protocol. That's unusual — many commercial insurance plans still
          classify accelerated TMS as experimental. The federal plan does not.
        </p>
        <p>
          But policy language isn't a guarantee of payment. SAINT uses newer
          billing codes that are still being adopted across the insurance
          industry. Your specific plan, your local Blue carrier, and your
          clinical documentation all matter. That's why we verify your benefits
          before you commit to anything.
        </p>
      </DecisionSection>

      <DecisionSection heading="What your plan looks for">
        <p>
          Your federal health plan uses three main criteria to decide whether
          TMS — including SAINT — is medically necessary:
        </p>
        <div className="mt-4 space-y-4">
          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
              <div>
                <h3 className="font-headings text-base font-bold text-primary">
                  1. Severe depression, confirmed
                </h3>
                <p className="mt-1 text-[16px] leading-relaxed text-muted-foreground">
                  Your diagnosis needs to be severe major depressive disorder,
                  documented by your provider using standard rating tools.
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
              <div>
                <h3 className="font-headings text-base font-bold text-primary">
                  2. Antidepressants haven't worked well enough
                </h3>
                <p className="mt-1 text-[16px] leading-relaxed text-muted-foreground">
                  You've tried at least two antidepressants from different
                  classes, for at least six weeks each at a proper dose, without
                  enough improvement. Or you couldn't tolerate them, or you've
                  responded to TMS before, or you're a candidate for ECT.
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
              <div>
                <h3 className="font-headings text-base font-bold text-primary">
                  3. You've also tried therapy
                </h3>
                <p className="mt-1 text-[16px] leading-relaxed text-muted-foreground">
                  You've had a trial of talk therapy known to be effective for
                  depression, for an adequate length of time, documented by your
                  provider.
                </p>
              </div>
            </div>
          </div>
        </div>
      </DecisionSection>

      <DecisionSection heading="What we do for you — at no cost">
        <p>
          Our team verifies your benefits before any financial commitment. We
          contact your federal health plan, confirm whether SAINT is covered
          under your specific option, check prior authorization requirements,
          and give you a written summary of your costs — all before you commit.
          We submit the authorization and handle appeals if it's denied.
        </p>
        <p>
          Call{" "}
          <a
            href="tel:9548279793"
            className="text-accent font-semibold hover:underline"
          >
            (954) 827-9793
          </a>{" "}
          or{" "}
          <Link
            to="/federal-employee-insurance-verification"
            className="text-accent hover:underline"
          >
            start the verification process
          </Link>
          .
        </p>
        <p className="text-sm text-muted-foreground pt-2">
          Coverage information current as of 2026. Federal medical policy is
          revised annually.
        </p>
      </DecisionSection>

      <InlineRelatedLinks heading="Related Pages" links={relatedLinks} />
    </DecisionPageLayout>
  );
}
